// Phase 15 Plan 09 Task 2 — TournamentJoinModal.
// ARCA-82 WS2 — migrated to <Dialog> primitive (scrim/panel/focus-trap live there).
//
// Bottom-sheet modal (role=dialog aria-modal=true Escape-close scrim-close)
// for joining a tournament by 8-char invite code.
//
// Recipe inherited from EmpowerBottomSheet / DeleteAccountModal:
//   - Scrim var(--color-scrim) covering the viewport (theme-aware)
//   - Card body --color-surface-mid + 3px --color-surface-stroke + 8px radius
//   - Header band with title
//
// Body:
//   - Text input (autoCapitalize=characters, spellCheck=false) for the code
//   - Paste-from-clipboard button → navigator.clipboard.readText() populates input
//   - Primary CTA Sun-Yellow chunky-pill "Join" (social.tournaments.join_cta)
//
// On submit:
//   - joinTournament(code) — net wrapper attaches Idempotency-Key (Plan 05)
//   - 200 → dispatch runegambit:toast (social.tournaments.joined) + runegambit:tournament-joined
//          → onClose()
//   - 404 → inline social.error.tournament_code_invalid
//   - 409 → inline social.error.tournament_full
//   - 423 → inline social.error.tournament_locked
//   - other 4xx → generic error
//   - 5xx → generic error (apiFetch already retried once)
//
// Note: navigator.clipboard.readText() requires a secure context AND a user
// gesture. The paste button click counts as the gesture; permission is
// requested inline. If readText rejects, we leave the input untouched and
// surface no inline error (the user can type instead — non-blocking).

import { type JSX, useId, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ApiError } from '../../net/client.js'
import { joinTournament } from '../../net/tournaments.js'
import { Dialog } from '../components/Dialog.js'

export interface TournamentJoinModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

const TOURNAMENT_JOINED_EVENT = 'runegambit:tournament-joined'
const TOAST_EVENT = 'runegambit:toast'

function dispatchToast(messageId: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, { detail: { id: messageId } }),
  )
}

function dispatchTournamentJoined(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(TOURNAMENT_JOINED_EVENT))
}

type InlineErrorKey =
  | 'social.error.tournament_code_invalid'
  | 'social.error.tournament_full'
  | 'social.error.tournament_locked'
  | 'social.error.tournament_generic'

function statusToErrorKey(status: number): InlineErrorKey {
  if (status === 404) return 'social.error.tournament_code_invalid'
  if (status === 409) return 'social.error.tournament_full'
  if (status === 423) return 'social.error.tournament_locked'
  return 'social.error.tournament_generic'
}

// WR-12: soft client-side pre-validation. The server is authoritative
// (oracle-safe 404 on malformed / unknown / locked / full), but disabling
// the submit on visibly-malformed input saves a round trip + surfaces a
// hint. Format is 8 chars from the Crockford-style alphabet [A-Z2-9].
const INVITE_CODE_RE = /^[A-Z2-9]{8}$/

export function TournamentJoinModal(
  props: TournamentJoinModalProps,
): JSX.Element | null {
  const { open, onClose } = props
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [inlineErrorKey, setInlineErrorKey] = useState<InlineErrorKey | null>(
    null,
  )
  const titleId = useId()
  const intl = useIntl()

  if (!open) return null

  function handlePaste(): void {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      return
    }
    navigator.clipboard
      .readText()
      .then((text) => {
        setCode(text.trim().toUpperCase())
      })
      .catch(() => {
        // Silent — clipboard permission denied. User can still type.
      })
  }

  async function handleSubmit(
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    e.preventDefault()
    if (submitting) return
    const trimmed = code.trim()
    if (trimmed.length === 0) return
    setSubmitting(true)
    setInlineErrorKey(null)
    try {
      await joinTournament(trimmed)
      dispatchToast('social.tournaments.joined')
      dispatchTournamentJoined()
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        setInlineErrorKey(statusToErrorKey(err.status))
      } else {
        setInlineErrorKey('social.error.tournament_generic')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + panel + focus trap + dismissal
    // live in <Dialog>/ModalScrim. padding:0 so the header band bleeds to the
    // rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onClose}
      testId="tournament-join-modal"
      labelledBy={titleId}
      dismissOnScrimTap={!submitting}
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-paper-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <h2
          id={titleId}
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-18)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.tournaments.join_modal.title"
            defaultMessage="Enter tournament code"
          />
        </h2>
      </div>
      <form
        onSubmit={(e) => {
          void handleSubmit(e)
        }}
        style={{
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage
            id="social.tournaments.join_modal.body"
            defaultMessage="Paste the code your host shared."
          />
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
          }}
        >
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value.toUpperCase())}
            data-testid="tournament-join-code-input"
            aria-label={intl.formatMessage({
              id: 'social.tournaments.join_modal.input_label',
              defaultMessage: 'Tournament code',
            })}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={16}
            placeholder={intl.formatMessage({
              id: 'social.tournaments.join_modal.code_placeholder',
            })}
            disabled={submitting}
            style={{
              flex: 1,
              height: '44px',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-3xl)',
              border: 'none',
              boxShadow:
                'inset 0 0 0 2px color-mix(in srgb, var(--color-surface-stroke) 55%, transparent)',
              backgroundColor: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontFeatureSettings: '"tnum" 1',
            }}
          />
          <button
            type="button"
            onClick={handlePaste}
            data-testid="tournament-join-paste"
            disabled={submitting}
            style={{
              height: '44px',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-3xl)',
              border: 'none',
              backgroundColor: 'var(--color-paper-2)',
              boxShadow: 'var(--shadow-tile)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-12)',
              cursor: submitting ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <FormattedMessage
              id="social.tournaments.join_modal.paste"
              defaultMessage="Paste"
            />
          </button>
        </div>
        {inlineErrorKey !== null ? (
          <p
            role="alert"
            data-testid="tournament-join-error"
            data-error-key={inlineErrorKey}
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-13)',
              color: 'var(--color-cta-destructive)',
            }}
          >
            <FormattedMessage
              id={inlineErrorKey}
              defaultMessage={defaultMessageFor(inlineErrorKey)}
            />
          </p>
        ) : null}
        <button
          type="submit"
          data-testid="tournament-join-submit"
          disabled={submitting || !INVITE_CODE_RE.test(code.trim())}
          className={`btn yellow lg${submitting || !INVITE_CODE_RE.test(code.trim()) ? ' disabled' : ''}`}
        >
          <FormattedMessage
            id="social.tournaments.join_cta"
            defaultMessage="Join"
          />
        </button>
      </form>
    </Dialog>
  )
}

function defaultMessageFor(key: InlineErrorKey): string {
  switch (key) {
    case 'social.error.tournament_code_invalid':
      return "That tournament code didn't match."
    case 'social.error.tournament_full':
      return 'This tournament is full.'
    case 'social.error.tournament_locked':
      return 'This tournament is locked.'
    case 'social.error.tournament_generic':
      return 'Something went wrong. Try again.'
  }
}
